import type {
  DemandCostsResult,
  DemandIssue,
  DemandIssueKind,
  DesignCategoryKey,
  DesignCategoryResult,
  DesignCostsResult,
  TrelloCard,
  TrelloCustomFieldDefinition,
  VideoCostsResult,
} from '@/types'
import {
  COVER_CARD_SHORT_LINKS,
  DESIGN_LABEL_NAME,
  QTD_TELAS_FIELD_NAME,
  VIDEO_LABEL_NAME,
} from '@/config/operations'
import { DESIGN_PRICING, VIDEO_PRICING } from '@/config/pricing'
import { normalise } from './labels'
import { cardDueInCycle } from './operations'

// ─── Display ─────────────────────────────────────────────────────────────────

export const DESIGN_CATEGORY_LABELS: Record<DesignCategoryKey, string> = {
  static: 'Estáticos',
  reelCover: 'Capas de Reel',
  carouselUpTo8: 'Carrosséis — até 8 telas',
  carousel9To10: 'Carrosséis — 9 a 10 telas',
  storySingle: 'Stories — 1 tela',
  story2Plus: 'Stories — 2 ou mais telas',
}

export const DEMAND_ISSUE_LABELS: Record<DemandIssueKind, string> = {
  'missing-telas': 'Informação incompleta',
  'telas-out-of-range': 'Valor não configurado',
  unclassified: 'Não classificada',
  'conflicting-type-labels': 'Etiqueta de Design e Vídeo ao mesmo tempo',
}

const DESIGN_CATEGORY_ORDER: DesignCategoryKey[] = [
  'static',
  'reelCover',
  'carouselUpTo8',
  'carousel9To10',
  'storySingle',
  'story2Plus',
]

const DESIGN_CATEGORY_PRICES: Record<DesignCategoryKey, number> = { ...DESIGN_PRICING }

// ─── Board lookups (resolved dynamically by name — never hardcoded IDs) ──────

function resolveCustomFieldId(
  fields: TrelloCustomFieldDefinition[],
  name: string
): string | null {
  const target = normalise(name)
  return fields.find((f) => normalise(f.name) === target)?.id ?? null
}

// ─── Card helpers ──────────────────────────────────────────────────────────

function isCoverCard(card: TrelloCard): boolean {
  return COVER_CARD_SHORT_LINKS.includes(card.shortLink)
}

function hasLabel(card: TrelloCard, normalisedName: string): boolean {
  return card.labels.some((l) => normalise(l.name) === normalisedName)
}

/**
 * Reads "Qtd. telas" from the card's customFieldItems.
 * Returns null when the field isn't set on the card — Trello simply omits
 * unset custom fields from the array, so this doubles as our "missing"
 * signal — or when the board's field couldn't be resolved at all.
 */
function getTelasCount(card: TrelloCard, telasFieldId: string | null): number | null {
  if (!telasFieldId) return null
  const item = card.customFieldItems?.find((i) => i.idCustomField === telasFieldId)
  const raw = item?.value?.number
  if (raw === undefined) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

function makeIssue(card: TrelloCard, kind: DemandIssueKind, detail: string): DemandIssue {
  return {
    kind,
    cardId: card.id,
    cardName: card.name,
    cardShortUrl: card.shortUrl,
    detail,
  }
}

// ─── Type classification (by LABEL, never by list/etapa) ──────────────────────

const DESIGN_LABEL_KEY = normalise(DESIGN_LABEL_NAME)
const VIDEO_LABEL_KEY = normalise(VIDEO_LABEL_NAME)

type CardType = 'design' | 'video' | 'conflict' | 'none'

/**
 * A card's LIST only reflects its current operational stage (Design/Vídeo →
 * Revisão final → Delivery Day → Agendamentos → Concluído...) — it changes
 * as the card moves through the pipeline. The card's TYPE (Design vs Vídeo)
 * is permanent and is given by its LABEL, independent of where it is now.
 */
function classifyCardType(card: TrelloCard): CardType {
  const isDesign = hasLabel(card, DESIGN_LABEL_KEY)
  const isVideo = hasLabel(card, VIDEO_LABEL_KEY)
  if (isDesign && isVideo) return 'conflict'
  if (isDesign) return 'design'
  if (isVideo) return 'video'
  return 'none'
}

// ─── Design classification (by FORMAT label, within Design-type cards) ───────

type DesignClassification =
  | { kind: 'priced'; category: DesignCategoryKey }
  | { kind: 'issue'; issue: DemandIssue }

/**
 * Classifies ONE Design card into a priced category or a pending issue.
 * 1 card = 1 demand — Qtd. telas only ever picks the price tier, never
 * multiplies the demand count. "Story" never gets reclassified as
 * "Carrossel", regardless of how many telas it has.
 */
function classifyDesignCard(
  card: TrelloCard,
  telasFieldId: string | null
): DesignClassification {
  const isStatic = hasLabel(card, 'estatico')
  const isReelCover = hasLabel(card, 'capa de reel')
  const isCarousel = hasLabel(card, 'carrossel')
  const isStory = hasLabel(card, 'story')

  if (isStatic) return { kind: 'priced', category: 'static' }
  if (isReelCover) return { kind: 'priced', category: 'reelCover' }

  if (isCarousel) {
    const telas = getTelasCount(card, telasFieldId)
    if (telas === null) {
      return { kind: 'issue', issue: makeIssue(card, 'missing-telas', 'Carrossel sem "Qtd. telas" preenchido') }
    }
    if (telas < 1) {
      return { kind: 'issue', issue: makeIssue(card, 'telas-out-of-range', `Carrossel com quantidade de telas inválida (${telas})`) }
    }
    if (telas <= 8) return { kind: 'priced', category: 'carouselUpTo8' }
    if (telas <= 10) return { kind: 'priced', category: 'carousel9To10' }
    return { kind: 'issue', issue: makeIssue(card, 'telas-out-of-range', `Carrossel com ${telas} telas — valor não configurado`) }
  }

  if (isStory) {
    const telas = getTelasCount(card, telasFieldId)
    if (telas === null) {
      return { kind: 'issue', issue: makeIssue(card, 'missing-telas', 'Story sem "Qtd. telas" preenchido') }
    }
    if (telas < 1) {
      return { kind: 'issue', issue: makeIssue(card, 'telas-out-of-range', `Story com quantidade de telas inválida (${telas})`) }
    }
    if (telas === 1) return { kind: 'priced', category: 'storySingle' }
    // 2 ou mais telas — sem limite superior, sempre story2Plus.
    return { kind: 'priced', category: 'story2Plus' }
  }

  return {
    kind: 'issue',
    issue: makeIssue(
      card,
      'unclassified',
      'Sem etiqueta de formato reconhecida (Estático, Capa de reel, Carrossel ou Story)'
    ),
  }
}

// ─── Design aggregation ────────────────────────────────────────────────────

function calculateDesignCosts(
  designCards: TrelloCard[],
  telasFieldId: string | null
): DesignCostsResult {
  const counts: Record<DesignCategoryKey, number> = {
    static: 0,
    reelCover: 0,
    carouselUpTo8: 0,
    carousel9To10: 0,
    storySingle: 0,
    story2Plus: 0,
  }
  const issues: DemandIssue[] = []

  for (const card of designCards) {
    const result = classifyDesignCard(card, telasFieldId)
    if (result.kind === 'priced') {
      counts[result.category] += 1
    } else {
      issues.push(result.issue)
    }
  }

  const categories: DesignCategoryResult[] = DESIGN_CATEGORY_ORDER.map((key) => ({
    key,
    label: DESIGN_CATEGORY_LABELS[key],
    count: counts[key],
    unitPrice: DESIGN_CATEGORY_PRICES[key],
    cost: counts[key] * DESIGN_CATEGORY_PRICES[key],
  }))

  const totalCost = categories.reduce((sum, c) => sum + c.cost, 0)
  // Every real card is a demand, priced or not.
  const totalCount = categories.reduce((sum, c) => sum + c.count, 0) + issues.length

  return { categories, totalCount, totalCost, issues }
}

// ─── Video aggregation ──────────────────────────────────────────────────────

function calculateVideoCosts(videoCards: TrelloCard[]): VideoCostsResult {
  const count = videoCards.length
  const { packageMinimum, packageUnitPrice, singleUnitPrice } = VIDEO_PRICING
  const meetsPackageMinimum = count >= packageMinimum

  let unitPrice: number | null
  let totalCost: number | null

  if (count === 0) {
    unitPrice = null
    totalCost = 0
  } else if (meetsPackageMinimum) {
    unitPrice = packageUnitPrice
    totalCost = count * packageUnitPrice
  } else {
    // Avulso price not defined yet — fill VIDEO_PRICING.singleUnitPrice in
    // src/config/pricing.ts and this starts pricing itself automatically.
    unitPrice = singleUnitPrice
    totalCost = singleUnitPrice === null ? null : count * singleUnitPrice
  }

  return { count, unitPrice, totalCost, packageMinimum, meetsPackageMinimum }
}

// ─── Main calculator ──────────────────────────────────────────────────────────

/**
 * Calculates outsourced-production demand counts and costs from the
 * Operação board, for the given PAYMENT CYCLE.
 *
 * Cards are classified as Design/Vídeo demands by LABEL, never by their
 * current list — a card's list only reflects its operational stage
 * (Design/Vídeo → Revisão final → Delivery Day → Agendamentos →
 * Concluído...) and changes throughout the cycle, while its type label is
 * permanent. `cards` should be every open card of the board (no list
 * filter applied before calling this).
 *
 * The period is a payment cycle (day 21 of the previous month through day
 * 20 of `month`/`year`, inclusive), not a calendar month — see
 * cardDueInCycle / getCycleRange in ./operations. The cycle is named after
 * `month`/`year`, which is the same content month already selected
 * elsewhere in the dashboard (see App.tsx) — no separate "previous month"
 * offset is applied here.
 */
export function calculateDemandCosts(
  cards: TrelloCard[],
  customFields: TrelloCustomFieldDefinition[],
  month: number,
  year: number,
  error?: string
): DemandCostsResult {
  if (error) {
    return {
      design: { categories: [], totalCount: 0, totalCost: 0, issues: [] },
      video: {
        count: 0,
        unitPrice: null,
        totalCost: 0,
        packageMinimum: VIDEO_PRICING.packageMinimum,
        meetsPackageMinimum: false,
      },
      knownTotalCost: 0,
      hasPendingCosts: false,
      error,
    }
  }

  const cycleCards = cards.filter((c) => cardDueInCycle(c, month, year))
  const realCards = cycleCards.filter((c) => !isCoverCard(c))
  const telasFieldId = resolveCustomFieldId(customFields, QTD_TELAS_FIELD_NAME)

  // Classify each card ONCE, by label, into exactly one bucket — a card
  // with both the Design and Vídeo labels goes to neither total (it would
  // otherwise be double-counted); it's flagged as a conflict instead.
  const designCards: TrelloCard[] = []
  const videoCards: TrelloCard[] = []
  const conflictCards: TrelloCard[] = []

  for (const card of realCards) {
    switch (classifyCardType(card)) {
      case 'design':
        designCards.push(card)
        break
      case 'video':
        videoCards.push(card)
        break
      case 'conflict':
        conflictCards.push(card)
        break
      case 'none':
        // Neither label — not a Design/Vídeo demand at all, correctly excluded.
        break
    }
  }

  const design = calculateDesignCosts(designCards, telasFieldId)
  for (const card of conflictCards) {
    design.issues.push(
      makeIssue(
        card,
        'conflicting-type-labels',
        'Card com etiqueta de Design e de Vídeo ao mesmo tempo — não contado em nenhum dos dois totais'
      )
    )
    design.totalCount += 1
  }

  const video = calculateVideoCosts(videoCards)

  const knownTotalCost = design.totalCost + (video.totalCost ?? 0)
  const hasPendingCosts = design.issues.length > 0 || video.totalCost === null

  return { design, video, knownTotalCost, hasPendingCosts }
}
